import { useEffect } from 'react';
import { useRouter } from 'next/router';
import { Card, Link, Select, Stack, Typography } from '@krakentech/coral';
import { Field, Formik, useFormikContext } from 'formik';

import {
  Alert,
  Body2Skeleton,
  EstimatedPaymentsCard,
  FormContainer,
  FormSubmitButton,
  FullWidthButtonSkeleton,
  H4Skeleton,
  PageError,
  PaymentDayField,
  RadioCard,
  RadioCardSkeleton,
  TextFieldSkeleton,
  NumberStepper,
  FormComponent,
} from '@/components';
import {
  PROPERTY_TYPE_OPTIONS,
  PropertyType,
  MAX_OCCUPANCY_NUMBER,
  MINIMUM_OCCUPANCY_NUMBER,
} from '@/consts/applyForMeter';
import {
  DIRECT_DEBIT_SCHEDULE_CHOICES,
  PAYMENT_FREQUENCY_FIELD_OPTIONS,
} from '@/consts/paymentFrequency';
import { useAccountNumberContext } from '@/context/AccountNumberContext/withFetch/AccountNumberContext';
import { OptionalDate } from '@/context/DirectDebitContext/index.types';
import { useBalance } from '@/hooks/accounts/useBalance';
import { usePropertyType } from '@/hooks/accounts/usePropertyType';
import { useUpdateNumberOfOccupants } from '@/hooks/accounts/useUpdateNumberOfOccupants';
import { useUpdatePropertyType } from '@/hooks/accounts/useUpdatePropertyType';
import { useProposedDirectDebitPaymentSchedule } from '@/hooks/billsAndPayments/useProposedDirectDebitPaymentSchedule';
import { useHasActiveWaterMeter } from '@/hooks/usage/useHasActiveWaterMeter';
import { useIsOnMeasuredTariff } from '@/hooks/usage/useIsOnMeasuredTariff';
import { useMeterReadings } from '@/hooks/usage/useMeterReadings';
import { useSyncFeatureFlag } from '@/hooks/utils/useFeatureFlags';
import { PaymentFrequencyFormValues } from '@/types/directDebit';
import { FeatureNames } from '@/types/features';
import { isStw } from '@/utils/environment';
import { formatFullDateShortMonth } from '@/utils/formatters/date';
import { INTERNAL_PATHS } from '@/utils/urls';

type PaymentFrequencyFormContentProps = {
  setFirstPossibleVariableSchedulePaymentDate: (date: OptionalDate) => void;
  setHasActiveWaterMeter: (hasActiveWaterMeter: boolean) => void;
  submitButtonLabel?: string;
  isChangePaymentScheduleForm?: boolean;
  isMutationError?: boolean;
  isMutationLoading?: boolean;
};

type PaymentFrequencyFormProps = PaymentFrequencyFormContentProps & {
  handleSubmit: (values: PaymentFrequencyFormValues) => void;
  initialPaymentFrequency: PAYMENT_FREQUENCY_FIELD_OPTIONS | undefined;
  initialPaymentDay: { label: string; value: number } | undefined;
  submitButtonLabel?: string;
  isChangePaymentScheduleForm?: boolean;
  isLoading?: boolean;
  isMutationError?: boolean;
  isMutationLoading?: boolean;
};

const PaymentFrequencyFormContent = ({
  setFirstPossibleVariableSchedulePaymentDate,
  setHasActiveWaterMeter,
  submitButtonLabel,
  isChangePaymentScheduleForm,
  isMutationError,
  isMutationLoading = false,
}: PaymentFrequencyFormContentProps) => {
  const { pathname } = useRouter();
  const { values, dirty } = useFormikContext<PaymentFrequencyFormValues>();
  const { accountNumber } = useAccountNumberContext();
  const {
    isError: isErrorProposedPaymentSchedule,
    refetch,
    data,
  } = useProposedDirectDebitPaymentSchedule({
    scheduleType:
      typeof values.paymentFrequency !== 'undefined' &&
      values.paymentFrequency === PAYMENT_FREQUENCY_FIELD_OPTIONS.VARIABLE
        ? DIRECT_DEBIT_SCHEDULE_CHOICES.PAY_ON_RECEIPT_OF_BILL
        : DIRECT_DEBIT_SCHEDULE_CHOICES.MONTHLY,
  });
  const {
    data: activeWaterMeterCount,
    isError: isErrorActiveWaterMeter,
    isLoading: isLoadingActiveWaterMeter,
  } = useHasActiveWaterMeter();

  const {
    data: balance,
    isLoading: isLoadingBalance,
    isError: isErrorBalance,
  } = useBalance();

  const {
    data: isMeasured,
    isLoading: isMeasuredLoading,
    isError: isMeasuredError,
    isSuccess: isMeasuredSuccess,
  } = useIsOnMeasuredTariff();

  const { data: propertyTypeData } = usePropertyType();

  const {
    data: meterReadingsData,
    isLoading: isLoadingMeterReadings,
    isError: isErrorMeterReadings,
  } = useMeterReadings({ accountNumber });

  const { mutate: mutatePropertyType } = useUpdatePropertyType();

  const payInFullOptionEnabled = useSyncFeatureFlag(FeatureNames.PayInFull);

  const isLoading =
    isLoadingBalance ||
    isLoadingActiveWaterMeter ||
    isMeasuredLoading ||
    isLoadingMeterReadings;

  const isError =
    isErrorBalance ||
    isErrorActiveWaterMeter ||
    isMeasuredError ||
    isErrorMeterReadings;

  const { firstPossibleCollectionDate } = data || {};

  const displayPayInFullOption =
    isStw() && isMeasuredSuccess && !isMeasured && payInFullOptionEnabled;

  const currentMonth = new Date().getMonth();
  // Can pay in full if the current month is January or February, or September or later. Otherwise, they pay in two instalments
  // FIXME: this would be better coming from the query when the BE work is done
  const canPayInOne = currentMonth < 2 || currentMonth > 8;

  // Block the user from setting up a fixed payment schedule if they're unmeasured and aren't in debit, as the mutation would error in this case
  const blockFixedPaymentSchedule = !activeWaterMeterCount && balance >= 0;

  const newEditDDUnmeasuredUIEnabled = useSyncFeatureFlag(
    FeatureNames.NewEditDirectDebitUnmeasuredUI
  );

  const setupDDPropertyDetailsEnabled = useSyncFeatureFlag(
    FeatureNames.SetupDDPropertyDetails
  );

  const changingPaymentSchedule = pathname.includes(
    INTERNAL_PATHS.CHANGE_PAYMENT_SCHEDULE.path
  );

  const hideEstimatedPaymentCardForNewUI =
    changingPaymentSchedule && newEditDDUnmeasuredUIEnabled;

  const hasNoPropertyType =
    !propertyTypeData?.freshWaterSupplyPoint?.propertyType;

  const numberOfReadings =
    meterReadingsData?.properties[0]?.activeWaterMeters[0]?.readings?.edges
      ?.length ?? 0;

  // Only show the property details card if FF enabled, the user has a measured tariff, is not changing the payment schedule and has less than 2 readings
  const showPropertyDetailsCard =
    !isLoadingMeterReadings &&
    setupDDPropertyDetailsEnabled &&
    isMeasured &&
    !isChangePaymentScheduleForm &&
    numberOfReadings < 2;

  // Display the estimated payments card if the user is on a variable schedule, a pay in full schedule or if they are on a monthly schedule and have selected a payment day. Or in other words, only display the estimated payments card if the above form fields have been filled in
  const displayEstimatedPaymentsCard =
    (values.paymentFrequency === PAYMENT_FREQUENCY_FIELD_OPTIONS.VARIABLE &&
      !hideEstimatedPaymentCardForNewUI) ||
    values.paymentFrequency ===
      PAYMENT_FREQUENCY_FIELD_OPTIONS.BIANNUAL_INSTALMENTS ||
    (values.paymentFrequency === PAYMENT_FREQUENCY_FIELD_OPTIONS.MONTHLY &&
      values.paymentDay?.value);

  const hasNoChanges = isChangePaymentScheduleForm && !dirty;
  // Disable the form submit button if the user is on a variable schedule and the proposed payment schedule is in error, or if the estimated payments card is not displayed. If the esimated payments card is not displayed, it means the user has not filled in the form fields
  const disableFormSubmitButton =
    (isErrorProposedPaymentSchedule &&
      values.paymentFrequency !== PAYMENT_FREQUENCY_FIELD_OPTIONS.VARIABLE) ||
    !displayEstimatedPaymentsCard ||
    (isChangePaymentScheduleForm && blockFixedPaymentSchedule) ||
    hasNoChanges;

  const { mutate: updateNumberOfOccupants } = useUpdateNumberOfOccupants();

  // Update the number of occupants for the property
  const handleOccupancyChange = (value: number) => {
    if (propertyTypeData?.id) {
      updateNumberOfOccupants(
        {
          input: {
            propertyId: propertyTypeData.id,
            numberOfOccupants: value,
          },
        },
        {
          onSuccess: () => {
            // Force a refetch of the proposed direct debit payment schedule to get updated numbers
            refetch();
          },
        }
      );
    }
  };

  // Save the default occupancy number of 1 on component mount, until mutation to retrieve occupancy is created
  useEffect(() => {
    if (showPropertyDetailsCard && propertyTypeData?.id) {
      handleOccupancyChange(1);
    }
  }, [showPropertyDetailsCard, propertyTypeData?.id]);

  // Save the firstPossiblePaymentDate to context if the user is on a variable schedule, so we can send it over the mutation later
  useEffect(() => {
    if (values.paymentFrequency === PAYMENT_FREQUENCY_FIELD_OPTIONS.VARIABLE) {
      setFirstPossibleVariableSchedulePaymentDate(firstPossibleCollectionDate);
    }
  }, [
    values.paymentFrequency,
    firstPossibleCollectionDate,
    setFirstPossibleVariableSchedulePaymentDate,
  ]);

  useEffect(() => {
    setHasActiveWaterMeter(!!activeWaterMeterCount);
  });

  if (isLoading) {
    return <LoadingState />;
  }

  if (isError) {
    return <PageError />;
  }

  return (
    <Stack gap="md" md={{ gap: 'lg' }} direction="vertical">
      {showPropertyDetailsCard && (
        <Card>
          <Stack direction="vertical" gap="md">
            <Typography variant="h3">About your property</Typography>
            <Typography>
              To ensure we set up the correct tariff for your home, please
              provide the following details.
            </Typography>
            {hasNoPropertyType && (
              <>
                <Typography variant="h3">Property type</Typography>
                <Select
                  name="propertyType"
                  label="Property type"
                  values={PROPERTY_TYPE_OPTIONS}
                  onChange={(option) => {
                    const propertyId = propertyTypeData?.id;
                    if (propertyId && option) {
                      mutatePropertyType({
                        input: {
                          propertyId,
                          propertyType: option.value as PropertyType,
                        },
                      });
                    }
                  }}
                />
              </>
            )}

            <Typography variant="h3">Number of occupants</Typography>
            <NumberStepper
              name="occupancyNumber"
              label="Occupancy"
              maxCount={MAX_OCCUPANCY_NUMBER}
              minCount={MINIMUM_OCCUPANCY_NUMBER}
              onIncrement={handleOccupancyChange}
              onDecrement={handleOccupancyChange}
            />
          </Stack>
        </Card>
      )}

      <Card>
        <Stack direction="vertical" gap="md">
          <Typography variant="h2">How often do you want to pay?</Typography>

          {displayPayInFullOption ? (
            <>
              <Field
                name="paymentFrequency"
                label="Pay monthly"
                explanation={
                  blockFixedPaymentSchedule
                    ? "You can't set up monthly payments right now. Please come back after you receive your next bill"
                    : 'Pay into your account with monthly regular payments'
                }
                component={RadioCard}
                value={PAYMENT_FREQUENCY_FIELD_OPTIONS.MONTHLY}
                dataTestId="monthlyScheduleField"
                onChange={refetch}
                disabled={blockFixedPaymentSchedule}
              />
              <Field
                name="paymentFrequency"
                label={canPayInOne ? 'Pay in full' : 'Pay in 2 instalments'}
                explanation={
                  canPayInOne
                    ? 'Pay your balance in full'
                    : 'Pay now and the rest in October'
                }
                component={RadioCard}
                value={PAYMENT_FREQUENCY_FIELD_OPTIONS.BIANNUAL_INSTALMENTS}
                dataTestId="variableScheduleField"
                onChange={refetch}
              />
            </>
          ) : (
            <>
              {isMeasured && (
                <Field
                  name="paymentFrequency"
                  label="When you get your bill"
                  explanation="We'll collect any balance owed in full after your bill has been issued"
                  component={RadioCard}
                  value={PAYMENT_FREQUENCY_FIELD_OPTIONS.VARIABLE}
                  dataTestId="variableScheduleField"
                  onChange={refetch}
                />
              )}
              <Field
                name="paymentFrequency"
                label="Monthly"
                explanation={
                  blockFixedPaymentSchedule
                    ? "You can't set up monthly payments right now. Please come back after you receive your next bill"
                    : 'Pay into your account with monthly regular payments'
                }
                component={RadioCard}
                value={PAYMENT_FREQUENCY_FIELD_OPTIONS.MONTHLY}
                dataTestId="monthlyScheduleField"
                onChange={refetch}
                disabled={blockFixedPaymentSchedule}
              />
            </>
          )}
        </Stack>
      </Card>

      {values.paymentFrequency === PAYMENT_FREQUENCY_FIELD_OPTIONS.MONTHLY && (
        <Card>
          <Stack direction="vertical" gap="md">
            <Typography variant="h2">Choose a payment date</Typography>

            <PaymentDayField />
          </Stack>
        </Card>
      )}

      {displayEstimatedPaymentsCard && <EstimatedPaymentsCard />}

      {isChangePaymentScheduleForm && firstPossibleCollectionDate && (
        <Alert severity={blockFixedPaymentSchedule ? 'warning' : 'info'}>
          {blockFixedPaymentSchedule ? (
            <Typography>
              You can&apos;t set up monthly payments right now. Please come back
              after you receive your next bill.
            </Typography>
          ) : (
            <Typography>
              Please note: changes won&apos;t update any payments coming out in
              the next 10 working days, and will become active from{' '}
              {formatFullDateShortMonth(firstPossibleCollectionDate)}.
            </Typography>
          )}
        </Alert>
      )}

      {values.paymentFrequency ===
        PAYMENT_FREQUENCY_FIELD_OPTIONS.BIANNUAL_INSTALMENTS && (
        <Alert severity="info">
          <Typography>
            Your scheduled payment will still be taken. If you change your
            payments now, you may overpay. To avoid this, wait until your
            payment clears, or contact your bank to stop the scheduled payment.
          </Typography>
        </Alert>
      )}

      {isMutationError && (
        <Alert severity="error">
          <Typography>
            Sorry, there seems to be a problem and we can&apos;t process your
            request right now. Please try again later or{' '}
            <Link href={process.env.NEXT_PUBLIC_HELP_LINK} target="_blank">
              contact us
            </Link>{' '}
            if the issue persists.
          </Typography>
        </Alert>
      )}

      <FormSubmitButton
        loading={isMutationLoading}
        disabled={disableFormSubmitButton}
      >
        {submitButtonLabel}
      </FormSubmitButton>
    </Stack>
  );
};

const PaymentFrequencyForm = ({
  setHasActiveWaterMeter,
  setFirstPossibleVariableSchedulePaymentDate,
  handleSubmit,
  initialPaymentFrequency,
  initialPaymentDay,
  submitButtonLabel = 'Add bank details',
  isChangePaymentScheduleForm,
  isLoading = false,
  isMutationError,
  isMutationLoading,
}: PaymentFrequencyFormProps) => {
  if (isLoading) return <LoadingState />;

  return (
    <FormContainer>
      <Formik
        initialValues={{
          paymentFrequency: initialPaymentFrequency,
          paymentDay: initialPaymentDay,
          occupancyNumber: 1,
        }}
        onSubmit={handleSubmit}
      >
        <FormComponent>
          <PaymentFrequencyFormContent
            submitButtonLabel={submitButtonLabel}
            setFirstPossibleVariableSchedulePaymentDate={
              setFirstPossibleVariableSchedulePaymentDate
            }
            setHasActiveWaterMeter={setHasActiveWaterMeter}
            isChangePaymentScheduleForm={isChangePaymentScheduleForm}
            isMutationError={isMutationError}
            isMutationLoading={isMutationLoading}
          />
        </FormComponent>
      </Formik>
    </FormContainer>
  );
};

const LoadingState = () => (
  <Stack gap="md" md={{ gap: 'lg' }} direction="vertical">
    <Card>
      <Stack direction="vertical" gap="md">
        <H4Skeleton />
        <RadioCardSkeleton />
        <RadioCardSkeleton />
      </Stack>
    </Card>

    <Card>
      <Stack direction="vertical" gap="md">
        <H4Skeleton />
        <Stack direction="vertical" gap="sm">
          <TextFieldSkeleton />

          <Body2Skeleton />
        </Stack>
      </Stack>
    </Card>

    <FullWidthButtonSkeleton />
  </Stack>
);

export default PaymentFrequencyForm;
